import { useCallback, useEffect, useRef } from "react";

const EDGE_THRESHOLD = 20; // px from left edge to start open gesture
const VELOCITY_THRESHOLD = 0.5; // px/ms to trigger snap
const DISTANCE_RATIO = 0.3; // 30% of sidebar width to trigger snap

type UseMobileSidebarSwipeOptions = {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    /** Ref to the sidebar panel — used for hit-testing and direct style updates during drag */
    sidebarRef: React.RefObject<HTMLDivElement | null>;
    /** Ref to the visual backdrop — opacity is updated directly during drag */
    backdropRef: React.RefObject<HTMLDivElement | null>;
    /** Width of the sidebar as a percentage of viewport width */
    sidebarWidthVw: number;
    enabled: boolean;
};

/**
 * Swipe-to-open / swipe-to-close for the mobile sidebar.
 *
 * During the drag phase, all DOM updates go through element.style directly
 * (zero React re-renders). At touchend, inline overrides are cleared and
 * React state is updated — React's own inline styles + CSS transitions
 * handle the snap animation.
 */
export function useMobileSidebarSwipe({
    isOpen,
    onOpen,
    onClose,
    sidebarRef,
    backdropRef,
    sidebarWidthVw,
    enabled,
}: UseMobileSidebarSwipeOptions): void {
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const touchStartTime = useRef(0);
    const isTracking = useRef(false);
    const directionLocked = useRef(false);
    const isHorizontal = useRef(false);
    const isDragging = useRef(false);
    const lastOffset = useRef(0);
    // Computed fresh at each touch-start so orientation changes are handled
    const sidebarWidthPx = useRef(0);

    // Ref-copy so event handlers always read the latest value without re-binding
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    const applyDragStyles = useCallback(
        (deltaX: number) => {
            const sidebar = sidebarRef.current;
            const backdrop = backdropRef.current;
            if (!sidebar) return;

            const width = sidebarWidthPx.current;
            let translateX: number;
            let progress: number; // 0 = fully closed, 1 = fully open

            if (isOpenRef.current) {
                translateX = Math.min(0, Math.max(deltaX, -width));
                progress = 1 + translateX / width;
            } else {
                const clamped = Math.max(0, Math.min(deltaX, width));
                translateX = -width + clamped;
                progress = clamped / width;
            }

            sidebar.style.transform = `translate3d(${translateX}px, 0, 0)`;
            sidebar.style.transitionProperty = "none";
            sidebar.style.visibility = "visible";

            if (backdrop) {
                backdrop.style.opacity = `${Math.max(0, Math.min(1, progress))}`;
                backdrop.style.transitionProperty = "none";
            }
        },
        [sidebarRef, backdropRef],
    );

    const handleTouchStart = useCallback(
        (e: TouchEvent) => {
            const touch = e.touches[0];
            if (!touch) return;

            // When closed: only start tracking from the left edge
            // When open: accept touches anywhere so swiping on the backdrop also closes
            if (!isOpenRef.current) {
                if (touch.clientX > EDGE_THRESHOLD) return;
            }

            sidebarWidthPx.current = (window.innerWidth * sidebarWidthVw) / 100;

            touchStartX.current = touch.clientX;
            touchStartY.current = touch.clientY;
            touchStartTime.current = Date.now();
            isTracking.current = true;
            directionLocked.current = false;
            isHorizontal.current = false;
        },
        [sidebarWidthVw],
    );

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (!isTracking.current) return;

            const touch = e.touches[0];
            if (!touch) return;

            const deltaX = touch.clientX - touchStartX.current;
            const deltaY = touch.clientY - touchStartY.current;

            if (!directionLocked.current) {
                if (Math.abs(deltaX) < 5 && Math.abs(deltaY) < 5) return;
                directionLocked.current = true;
                isHorizontal.current = Math.abs(deltaX) > Math.abs(deltaY);
                if (!isHorizontal.current) {
                    isTracking.current = false;
                    return;
                }
            }

            if (!isHorizontal.current) return;

            e.preventDefault();
            isDragging.current = true;
            lastOffset.current = deltaX;

            applyDragStyles(deltaX);
        },
        [applyDragStyles],
    );

    const handleTouchEnd = useCallback(() => {
        if (!isTracking.current || !isDragging.current) {
            isTracking.current = false;
            isDragging.current = false;
            lastOffset.current = 0;
            return;
        }

        isTracking.current = false;
        isDragging.current = false;

        const offset = lastOffset.current;
        lastOffset.current = 0;

        const width = sidebarWidthPx.current;
        const elapsed = Date.now() - touchStartTime.current;
        const velocity = Math.abs(offset) / Math.max(elapsed, 1);
        const threshold = width * DISTANCE_RATIO;

        let shouldChange = false;
        if (!isOpenRef.current) {
            shouldChange = offset > threshold || (offset > 0 && velocity > VELOCITY_THRESHOLD);
        } else {
            shouldChange = offset < -threshold || (offset < 0 && velocity > VELOCITY_THRESHOLD);
        }

        const sidebar = sidebarRef.current;
        const backdrop = backdropRef.current;

        // Clear all inline style overrides from the drag phase. This returns
        // the elements to React's current inline styles (the "before" state).
        if (sidebar) {
            sidebar.style.transform = "";
            sidebar.style.transitionProperty = "";
            sidebar.style.visibility = "";
        }
        if (backdrop) {
            backdrop.style.opacity = "";
            backdrop.style.transitionProperty = "";
        }

        // Now trigger the state change. React re-renders with new inline
        // styles (e.g. transform changes from 0 to -100% or vice versa),
        // and the CSS transition-transform on the element animates the change.
        if (shouldChange) {
            if (!isOpenRef.current) {
                onOpen();
            } else {
                onClose();
            }
        }
    }, [onOpen, onClose, sidebarRef, backdropRef]);

    useEffect(() => {
        if (!enabled) return;

        document.addEventListener("touchstart", handleTouchStart, { passive: true });
        document.addEventListener("touchmove", handleTouchMove, { passive: false });
        document.addEventListener("touchend", handleTouchEnd, { passive: true });

        return () => {
            document.removeEventListener("touchstart", handleTouchStart);
            document.removeEventListener("touchmove", handleTouchMove);
            document.removeEventListener("touchend", handleTouchEnd);
        };
    }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);
}
